"use client";

import { useActionState, useEffect, useState } from "react";
import { submitVdpLead, type SubmitState } from "./actions";
import { fireLeadSubmit } from "@/lib/analytics/events";

const initial: SubmitState = { status: "idle" };

export function VdpLeadForm({ vehicleId }: { vehicleId: string }) {
  const [state, formAction, pending] = useActionState(submitVdpLead, initial);
  const [renderTs] = useState(() => Date.now());

  useEffect(() => {
    if (state.status === "ok") {
      fireLeadSubmit({
        formId: "vdp_contact",
        formType: "vdp_contact",
        vehicleId,
        pagePath: typeof window !== "undefined" ? window.location.pathname : "/",
      });
    }
  }, [state.status, vehicleId]);

  const fe = state.fieldErrors ?? {};
  const inputCls =
    "w-full rounded-[--radius-sm] border border-[--color-border-strong] bg-[--color-background] px-3 py-2 text-sm";

  if (state.status === "ok") {
    return (
      <div className="rounded-[--radius-md] border border-emerald-500/30 bg-emerald-500/10 p-6 text-sm text-emerald-200">
        {state.message ?? "Thanks — we'll be in touch."}
      </div>
    );
  }

  return (
    <form action={formAction} className="flex flex-col gap-3">
      <input type="hidden" name="vehicleId" value={vehicleId} />
      <input type="hidden" name="renderTs" value={renderTs} />
      {/* honeypot */}
      <label className="sr-only" aria-hidden>
        <span>Company</span>
        <input name="company" tabIndex={-1} autoComplete="off" />
      </label>

      <Field name="fullName" label="Full name" required errors={fe.fullName} cls={inputCls} />
      <Field name="email" type="email" label="Email" required errors={fe.email} cls={inputCls} />
      <Field name="phone" type="tel" label="Phone" errors={fe.phone} cls={inputCls} />
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-[--color-foreground-muted]">Message</span>
        <textarea
          name="message"
          rows={4}
          className={`${inputCls} min-h-[100px]`}
          placeholder="Is this still available?"
        />
      </label>
      <label className="mt-1 flex items-center gap-2 text-xs text-[--color-foreground-muted]">
        <input type="checkbox" name="consentMarketing" />
        Send me updates about new arrivals.
      </label>

      {state.status === "error" && state.message ? (
        <div className="rounded-[--radius-sm] border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
          {state.message}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={pending}
        className="mt-2 rounded-[--radius-sm] bg-[--color-brand-accent] px-5 py-2.5 text-sm font-medium text-[--color-on-accent] disabled:opacity-50"
      >
        {pending ? "Sending…" : "Send inquiry"}
      </button>
    </form>
  );
}

function Field({
  name,
  label,
  type = "text",
  required,
  errors,
  cls,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  errors?: string[];
  cls: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-[--color-foreground-muted]">
        {label}
        {required ? " *" : ""}
      </span>
      <input name={name} type={type} required={required} className={cls} />
      {errors?.length ? <span className="text-xs text-amber-400">{errors[0]}</span> : null}
    </label>
  );
}
